/*
 * Lieferort-Auswahl ("📍 Liefern nach 84028 Landshut"), wie bei Amazon: Land + PLZ.
 *
 * Markup: snippets/altruan-lieferort.liquid (Kopfzeile und Preisblock). Zustand und Speicherung:
 * window.AltruanLieferort aus snippets/altruan-lieferort-head.liquid. Eine Änderung löst das
 * Document-Event "altruan-lieferort:change" aus; Preisblock, Produktkarten und Liefer-Timeline laden
 * daraufhin ihre Liefertermine mit Land und PLZ neu (Label "… · nach 84028 Landshut" kommt von der App).
 *
 * Land: Auswahl aus den Ländern von delivery_times.json (Shipping-App, GET /public-delivery-countries,
 * eine Stunde in sessionStorage); ändert nur die Liefertermine, nicht den Shopify-Markt. Vorgewählt:
 * gespeicherter Ort, sonst Standardadresse, sonst Markt. Ohne Antwort der App steht nur der Markt da.
 * Ort: aus der gewählten Kundenadresse, bei einer eingetippten PLZ von der App (city); "Übernehmen"
 * fragt dafür einmal den Liefertermin an, auch auf Seiten ohne Liefertermine.
 *
 * Das Fenster ist ein einfaches Popover wie das Versand-Info-Fenster im Preisblock:
 * Esc und Klick daneben schließen es, der Fokus geht zurück auf den Knopf. Auf kleinen Bildschirmen
 * öffnet es als Leiste am unteren Rand in der obersten Ebene (Popover-API): so liegt es über dem
 * Cookie-Knopf und dem Kopf, und der Hintergrund wird abgedunkelt (::backdrop).
 *
 * Eingeloggte B2B-Kunden (window.AltruanLieferort.location): Knopf "Liefern an <Standort> · PLZ",
 * im Fenster nur die Standortliste, kein PLZ-Feld.
 */
(() => {
  if (customElements.get("altruan-lieferort")) return;

  // PLZ-Formate der eigenen Märkte; sonst nur die Grundregel der Shipping-App
  const FORMATS = { DE: /^\d{5}$/, AT: /^\d{4}$/, CH: /^\d{4}$/ };
  const GENERAL = /^[A-Za-z0-9][A-Za-z0-9 -]{1,9}$/;
  let counter = 0;

  const api = () => window.AltruanLieferort || null;

  const isValid = (zip, country) => GENERAL.test(zip) && (!FORMATS[country] || FORMATS[country].test(zip));

  // "84028 Landshut": PLZ + Ort (Kundenadresse oder von der App genannt)
  const placeText = (place) => [place.postal_code, api().shownCity(place)].filter(Boolean).join(" ");

  const COUNTRIES_KEY = "altruan-lieferort:countries";
  const COUNTRIES_MS = 60 * 60 * 1000;
  let countriesRequest = null;

  // Länder aus delivery_times.json (["DE", "AT", …]); [] wenn die App nicht antwortet
  const loadCountries = () => {
    if (countriesRequest) return countriesRequest;
    try {
      const hit = JSON.parse(sessionStorage.getItem(COUNTRIES_KEY) || "null");
      if (hit && Date.now() < hit.until && Array.isArray(hit.countries))
        return (countriesRequest = Promise.resolve(hit.countries));
    } catch (_) {}
    const url = api().countriesUrl;
    countriesRequest = (url ? fetch(url).then((res) => (res.ok ? res.json() : null)) : Promise.resolve(null))
      .then((data) => {
        const list = data && Array.isArray(data.countries) ? data.countries.filter((c) => /^[A-Z]{2}$/.test(c)) : [];
        if (!list.length) countriesRequest = null;
        try {
          if (list.length)
            sessionStorage.setItem(
              COUNTRIES_KEY,
              JSON.stringify({ until: Date.now() + COUNTRIES_MS, countries: list }),
            );
        } catch (_) {}
        return list;
      })
      .catch(() => {
        countriesRequest = null;
        return [];
      });
    return countriesRequest;
  };

  let regionNames = null;
  const countryName = (code) => {
    if (code === api().country && api().countryName) return api().countryName;
    try {
      regionNames = regionNames || new Intl.DisplayNames([document.documentElement.lang || "de"], { type: "region" });
      return regionNames.of(code) || code;
    } catch (_) {
      return code;
    }
  };

  const small = window.matchMedia("(max-width: 749px)");

  class AltruanLieferort extends HTMLElement {
    connectedCallback() {
      if (!api() || this.ready) return;
      this.ready = true;

      try {
        this.t = JSON.parse(this.querySelector("[data-lieferort-t]").textContent);
      } catch (_) {
        this.t = {};
      }
      this.variant = this.dataset.variant || "inline";
      this.btn = this.querySelector("[data-lieferort-btn]");
      this.pop = this.querySelector("[data-lieferort-pop]");
      this.form = this.querySelector("[data-lieferort-form]");
      this.zip = this.querySelector("[data-lieferort-zip]");
      this.countrySelect = this.querySelector("[data-lieferort-country]");
      this.error = this.querySelector("[data-lieferort-error]");
      this.removeBtn = this.querySelector("[data-lieferort-remove]");
      this.extra = this.querySelector("[data-lieferort-extra]");

      counter += 1;
      this.pop.id = `Lieferort-${counter}`;
      const title = this.querySelector("[data-lieferort-title]");
      title.id = `Lieferort-${counter}-title`;
      this.pop.setAttribute("aria-labelledby", title.id);
      this.btn.setAttribute("aria-controls", this.pop.id);

      this.btn.addEventListener("click", () => (this.pop.hidden ? this.open() : this.close(true)));
      this.querySelector("[data-lieferort-close]").addEventListener("click", () => this.close(true));
      // B2B-Fenster hat kein Formular (nur Standortliste)
      if (this.form) {
        this.form.addEventListener("submit", (e) => {
          e.preventDefault();
          this.apply();
        });
        this.zip.addEventListener("input", () => this.showError(false));
        this.countrySelect.addEventListener("change", () => {
          this.showError(false);
          this.setInputMode();
        });
        this.removeBtn.addEventListener("click", () => {
          api().clear();
          this.close(true);
        });
      }
      // Klick auf die Abdunklung (::backdrop) trifft das Fenster selbst, außerhalb seiner Fläche
      this.pop.addEventListener("click", (e) => {
        if (e.target !== this.pop) return;
        const r = this.pop.getBoundingClientRect();
        const inside = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (!inside) this.close(true);
      });

      this.onKey = (e) => {
        if (e.key !== "Escape" || this.pop.hidden) return;
        this.close(true);
        this.closedByEscape = true;
      };
      // Im mobilen Menü schließt Esc sonst auch das Menü (menu-drawer hört auf keyup)
      this.addEventListener("keyup", (e) => {
        if (e.key !== "Escape" || !this.closedByEscape) return;
        this.closedByEscape = false;
        e.stopPropagation();
      });
      this.onOutside = (e) => {
        if (!this.pop.hidden && !this.contains(e.target)) this.close(false);
      };
      this.onChange = () => this.render();
      document.addEventListener("keydown", this.onKey);
      document.addEventListener("click", this.onOutside);
      document.addEventListener("altruan-lieferort:change", this.onChange);
      document.addEventListener("altruan-lieferort:city", this.onChange);

      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener("keydown", this.onKey);
      document.removeEventListener("click", this.onOutside);
      document.removeEventListener("altruan-lieferort:change", this.onChange);
      document.removeEventListener("altruan-lieferort:city", this.onChange);
      this.ready = false;
    }

    // Knopftext: Kopfzeile/Menü zweizeilig ("Liefern nach" / "84028 Landshut"), mobile Leiste einzeilig,
    // Preisblock nur der Ort ("Landshut", Link neben dem Liefertermin). B2B: "Liefern an" / "<Standort> · 84028".
    render() {
      const place = api().get();
      const location = api().location;
      const smallText = this.querySelector("[data-lieferort-small]");
      const main = this.querySelector("[data-lieferort-place]");
      const text = place ? placeText(place) : "";
      const set = !!(place || location);

      if (this.variant === "inline") {
        smallText.hidden = true;
        main.textContent = location
          ? location.name
          : place
            ? api().shownCity(place) || place.postal_code
            : this.t.choose || "";
      } else {
        smallText.textContent = location ? this.t.deliverToLocation || "" : place ? this.t.deliverTo || "" : "";
        smallText.hidden = !set;
        main.textContent = location ? location.name : place ? text : this.t.choose || "";
        if (location && location.postal_code) {
          const zip = document.createElement("span");
          zip.className = "lieferort__zip";
          zip.textContent = ` · ${location.postal_code}`;
          main.append(zip);
        }
      }
      if (this.extra) this.extra.hidden = set;

      const label = location
        ? `${this.t.deliverToLocation || ""} ${location.name} ${location.postal_code || ""}`
        : place
          ? `${this.t.deliverTo || ""} ${text} – ${this.t.change || ""}`
          : this.t.choose || "";
      this.btn.setAttribute("aria-label", label.replace(/\s+/g, " ").trim());
      this.classList.toggle("lieferort--set", set);
    }

    // Kundenadressen in Ländern der Auswahl; Adressen außerhalb des Markts nennen ihr Land
    renderAddresses(current, countries) {
      const box = this.querySelector("[data-lieferort-saved]");
      const list = this.querySelector("[data-lieferort-addresses]");
      const addresses = (api().addresses || []).filter((address) => countries.includes(address.country));
      box.hidden = addresses.length === 0;
      list.textContent = "";
      addresses.forEach((address) => {
        const item = document.createElement("button");
        item.type = "button";
        item.className = "lieferort__address";
        const active = !!current && current.postal_code === address.postal_code && current.country === address.country;
        item.setAttribute("aria-pressed", String(active));
        const line = document.createElement("span");
        line.textContent = address.line || "";
        const where = document.createElement("strong");
        where.textContent = [
          address.postal_code,
          address.city,
          address.country !== api().country ? countryName(address.country) : "",
        ]
          .filter(Boolean)
          .join(" ");
        item.append(where, line);
        item.addEventListener("click", () => {
          api().set({ country: address.country, postal_code: address.postal_code, city: address.city });
          this.close(true);
        });
        list.append(item);
      });
    }

    open() {
      document.querySelectorAll("altruan-lieferort").forEach((other) => {
        if (other !== this && other.pop && !other.pop.hidden) other.close(false);
      });
      this.pop.hidden = false;
      this.btn.setAttribute("aria-expanded", "true");
      if (small.matches && this.pop.showPopover) {
        this.pop.popover = "manual";
        this.pop.showPopover();
        this.liftOverKeyboard(true);
      }
      if (!this.form) {
        const first = this.pop.querySelector(".lieferort__address");
        if (first) first.focus();
        else this.pop.querySelector("[data-lieferort-close]").focus();
        return;
      }
      const place = api().get();
      this.zip.value = place ? place.postal_code : "";
      this.removeBtn.hidden = !place;
      this.showError(false);
      const selected = (place && place.country) || api().country;
      this.renderCountries([], selected);
      this.renderAddresses(place, [selected]);
      this.zip.focus();
      loadCountries().then((countries) => {
        if (this.pop.hidden) return;
        this.renderCountries(countries, this.countrySelect.value || selected);
        this.renderAddresses(place, this.countryCodes());
      });
    }

    // Optionen: Länder der App, dazu immer Markt und gewähltes Land; nach Namen sortiert
    renderCountries(countries, selected) {
      const codes = Array.from(new Set([...countries, api().country, selected]));
      const names = codes.map((code) => [code, countryName(code)]).sort((a, b) => a[1].localeCompare(b[1]));
      this.countrySelect.textContent = "";
      names.forEach(([code, name]) => this.countrySelect.add(new Option(name, code, false, code === selected)));
      this.countrySelect.value = selected;
      this.setInputMode();
    }

    countryCodes() {
      return Array.from(this.countrySelect.options, (option) => option.value);
    }

    // Ziffernblock nur für Länder mit reinen Ziffern-PLZ
    setInputMode() {
      if (FORMATS[this.countrySelect.value]) this.zip.setAttribute("inputmode", "numeric");
      else this.zip.removeAttribute("inputmode");
    }

    // Bottom-Sheet über der Bildschirmtastatur halten: die Tastatur verkleinert nur den sichtbaren
    // Ausschnitt (visualViewport), nicht den Bereich, an dem position: fixed klebt.
    liftOverKeyboard(on) {
      const vv = window.visualViewport;
      if (!vv) return;
      if (!this.onViewport) {
        this.onViewport = () => {
          const kb = Math.max(0, window.innerHeight - vv.height - vv.offsetTop);
          this.pop.style.setProperty("--lo-kb", `${Math.round(kb)}px`);
        };
      }
      vv.removeEventListener("resize", this.onViewport);
      vv.removeEventListener("scroll", this.onViewport);
      if (on) {
        vv.addEventListener("resize", this.onViewport);
        vv.addEventListener("scroll", this.onViewport);
        this.onViewport();
      } else {
        this.pop.style.removeProperty("--lo-kb");
      }
    }

    close(focusBack) {
      this.liftOverKeyboard(false);
      if (this.pop.popover) {
        if (this.pop.matches(":popover-open")) this.pop.hidePopover();
        this.pop.removeAttribute("popover");
      }
      this.pop.hidden = true;
      this.btn.setAttribute("aria-expanded", "false");
      if (focusBack) this.btn.focus();
    }

    apply() {
      const zip = this.zip.value.trim().replace(/\s+/g, " ");
      const country = this.countrySelect.value || api().country;
      if (!isValid(zip, country)) {
        this.showError(true);
        this.zip.focus();
        return;
      }
      api().set({ country, postal_code: zip.toUpperCase() });
      this.close(true);
      // Eine Anfrage bringt den Ort zur PLZ (für den Knopf), auch auf Seiten ohne Liefertermine
      if (window.AltruanLieferdatum) window.AltruanLieferdatum.info({ country, inStock: true });
    }

    showError(show) {
      this.error.hidden = !show;
      if (show) this.zip.setAttribute("aria-invalid", "true");
      else this.zip.removeAttribute("aria-invalid");
    }
  }

  customElements.define("altruan-lieferort", AltruanLieferort);
})();
